import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CURRENCIES } from "@/lib/api";

const OCCASIONS = [
  { key: "bridal", label: "Bridal" },
  { key: "festive", label: "Festive" },
  { key: "cocktail", label: "Cocktail" },
  { key: "everyday", label: "Everyday" },
];
const CATEGORIES = [
  { key: "anarkali", label: "Anarkalis" },
  { key: "lehenga", label: "Lehengas" },
  { key: "saree", label: "Sarees" },
  { key: "suit", label: "Suits" },
  { key: "kaftan", label: "Kaftans" },
  { key: "co-ord", label: "Co-Ord Sets" },
];
const CRAFTS = [
  { key: "dabka", label: "Dabka Work" },
  { key: "zardozi", label: "Zardozi" },
  { key: "mirror", label: "Mirror Work" },
];

export default function Header({ onOpenSearch }) {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverMenu, setHoverMenu] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setHoverMenu(null); }, [location.pathname]);

  const isAdminRoute = location.pathname.startsWith("/admin");
  if (isAdminRoute) return null;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#BCA37F]/25" : "bg-[#FDFBF7]/70 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="px-6 md:px-12 lg:px-16">
        {/* Top row */}
        <div className="flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-4 md:hidden">
            <button
              className="p-1"
              onClick={() => setMobileOpen(true)}
              data-testid="nav-mobile-menu"
              aria-label="Open menu"
            >
              <Menu strokeWidth={1.4} />
            </button>
          </div>

          {/* Desktop left nav */}
          <nav className="hidden md:flex items-center gap-8">
            {[{ label: "Bridal", key: "bridal" }, { label: "Festive", key: "festive" }].map((o) => (
              <Link
                key={o.key}
                to={`/collections/${o.key}`}
                className="text-[11px] tracking-nav uppercase text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors"
                data-testid={`nav-occasion-${o.key}`}
              >
                {o.label}
              </Link>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setHoverMenu("shop")}
              onMouseLeave={() => setHoverMenu(null)}
            >
              <button
                className="text-[11px] tracking-nav uppercase text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors flex items-center gap-1"
                data-testid="nav-shop"
              >
                Shop <ChevronDown size={12} strokeWidth={1.5} />
              </button>
            </div>
            <Link
              to="/blog"
              className="text-[11px] tracking-nav uppercase text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors"
              data-testid="nav-journal"
            >
              Journal
            </Link>
          </nav>

          {/* Logo */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 font-serif text-2xl md:text-3xl tracking-[0.3em] text-[#1A1A1A]"
            data-testid="nav-logo"
          >
            DABKAZARI
          </Link>

          {/* Right */}
          <div className="ml-auto flex items-center gap-4 md:gap-6">
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="hidden sm:block bg-transparent text-[11px] tracking-nav uppercase text-[#4A4A4A] border-none focus:outline-none cursor-pointer"
              data-testid="nav-currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={onOpenSearch}
              aria-label="Search"
              className="text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors"
              data-testid="nav-search"
            >
              <Search size={18} strokeWidth={1.4} />
            </button>
            {user ? (
              <div className="relative group">
                <button className="text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors" data-testid="nav-account">
                  <User size={18} strokeWidth={1.4} />
                </button>
                <div className="absolute right-0 top-full pt-3 hidden group-hover:block">
                  <div className="bg-[#FDFBF7] border border-[#BCA37F]/30 min-w-[180px] py-2 shadow-sm">
                    <div className="px-4 py-2 text-[11px] uppercase tracking-nav text-[#7A7A7A] font-serif">
                      Hi, {user.name?.split(" ")[0]}
                    </div>
                    <Link to="/account" className="block px-4 py-2 text-sm hover:bg-[#F3EFEA]" data-testid="nav-account-orders">Orders</Link>
                    <Link to="/wishlist" className="block px-4 py-2 text-sm hover:bg-[#F3EFEA]" data-testid="nav-account-wishlist">Wishlist</Link>
                    {user.role === "admin" && (
                      <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-[#F3EFEA]" data-testid="nav-account-admin">Admin</Link>
                    )}
                    <button
                      onClick={async () => { await logout(); navigate("/"); }}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-[#F3EFEA]"
                      data-testid="nav-logout"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" aria-label="Sign in" className="text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors" data-testid="nav-login">
                <User size={18} strokeWidth={1.4} />
              </Link>
            )}
            <Link to="/wishlist" aria-label="Wishlist" className="text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors hidden sm:inline" data-testid="nav-wishlist">
              <Heart size={18} strokeWidth={1.4} />
            </Link>
            <button
              onClick={() => setOpen(true)}
              className="relative text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors"
              data-testid="nav-cart"
              aria-label="Cart"
            >
              <ShoppingBag size={18} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-2 text-[9px] bg-[#5C1A1B] text-[#FDFBF7] rounded-full w-4 h-4 flex items-center justify-center" data-testid="nav-cart-count">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Second row — categories, desktop only */}
        <nav className="hidden md:flex items-center justify-center gap-10 py-3 border-t border-[#BCA37F]/20">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/shop/${c.key}`}
              className="text-[10.5px] tracking-nav uppercase text-[#4A4A4A] hover:text-[#5C1A1B] transition-colors"
              data-testid={`nav-cat-${c.key}`}
            >
              {c.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#FDFBF7]" data-testid="mobile-menu">
          <div className="flex items-center justify-between px-6 h-16 border-b border-[#BCA37F]/20">
            <span className="font-serif tracking-[0.3em] text-lg">DABKAZARI</span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X strokeWidth={1.4} /></button>
          </div>
          <div className="px-6 py-8 space-y-8">
            <div>
              <p className="font-serif text-sm text-[#7A7A7A] mb-3 uppercase tracking-nav">Shop by Occasion</p>
              <ul className="space-y-3">
                {OCCASIONS.map((o) => (
                  <li key={o.key}>
                    <Link to={`/collections/${o.key}`} className="font-serif text-2xl">{o.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-serif text-sm text-[#7A7A7A] mb-3 uppercase tracking-nav">Shop by Category</p>
              <ul className="space-y-3">
                {CATEGORIES.map((c) => (
                  <li key={c.key}>
                    <Link to={`/shop/${c.key}`} className="font-serif text-2xl">{c.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Desktop hover mega menu */}
      {hoverMenu === "shop" && (
        <div
          className="hidden md:block absolute left-0 right-0 top-full bg-[#FDFBF7] border-t border-[#BCA37F]/20 border-b border-[#BCA37F]/20 shadow-sm"
          onMouseEnter={() => setHoverMenu("shop")}
          onMouseLeave={() => setHoverMenu(null)}
        >
          <div className="px-16 py-10 grid grid-cols-3 gap-10 max-w-7xl mx-auto">
            <div>
              <p className="font-serif text-xs uppercase tracking-nav text-[#5C1A1B] mb-4">By Occasion</p>
              <ul className="space-y-2">
                {OCCASIONS.map((o) => (
                  <li key={o.key}>
                    <Link className="text-sm hover:text-[#5C1A1B] transition-colors" to={`/collections/${o.key}`}>{o.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-serif text-xs uppercase tracking-nav text-[#5C1A1B] mb-4">By Category</p>
              <ul className="space-y-2">
                {CATEGORIES.map((c) => (
                  <li key={c.key}>
                    <Link className="text-sm hover:text-[#5C1A1B] transition-colors" to={`/shop/${c.key}`}>{c.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-serif text-xs uppercase tracking-nav text-[#5C1A1B] mb-4">By Craft</p>
              <ul className="space-y-2">
                {CRAFTS.map((c) => (
                  <li key={c.key}>
                    <Link className="text-sm hover:text-[#5C1A1B] transition-colors" to={`/craft/${c.key}`}>{c.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
